import moment from 'moment';
import {
  ISO_DATE_FORMAT,
  LONG_DATE_FORMAT,
  MOMENT_DEFAULT_DATE_FORMAT,
  MOMENT_SUPPORTED_DATE_FORMATS,
  MOMENT_SUPPORTED_DATE_TIME_FORMATS,
} from './constants';

interface FormatDateTimeOptions {
  timeZone?: string;
  hour12?: boolean;
  month?: 'numeric' | '2-digit' | 'long' | 'short' | 'narrow';
  day?: 'numeric' | '2-digit';
  year?: 'numeric' | '2-digit';
  hour?: 'numeric' | '2-digit';
  minute?: 'numeric' | '2-digit';
}

export const shortDate = (date?: moment.MomentInput, separator = '/'): string | null => {
  if (!date) {
    return null;
  }

  const dateParsed = moment(date, MOMENT_SUPPORTED_DATE_FORMATS, true);
  const dateFormat = `MM${separator}DD${separator}YYYY`;

  return dateParsed.isValid() ? dateParsed.format(dateFormat) : null;
};

export const getYearFromDate = (date: moment.MomentInput): number | null => {
  const dateParsed = moment(date, MOMENT_SUPPORTED_DATE_FORMATS, true);

  return dateParsed.isValid() ? dateParsed.year() : null;
};

export const reconstructDate = (month: string | number = '12', day: string | number = '31'): string => {
  const currentYear = moment().year();
  const mmnt = moment(`${currentYear}-${month}-${day}`, ISO_DATE_FORMAT);
  // quick fix for DatePicker bug
  return new Date(mmnt.toDate()).toISOString();
};

export const gridShortDate = (date?: moment.MomentInput) =>
  moment(date || moment(), MOMENT_SUPPORTED_DATE_FORMATS).format(MOMENT_DEFAULT_DATE_FORMAT);

export const gridLongDate = (date?: moment.MomentInput) =>
  moment(date ?? moment(), MOMENT_SUPPORTED_DATE_TIME_FORMATS).format('MM/DD/YYYY HH:mm');

export const gridLocalShortDate = (date: moment.MomentInput) => moment(date).local().format(MOMENT_DEFAULT_DATE_FORMAT);

export const dbShortDate = (date?: moment.MomentInput, format = ISO_DATE_FORMAT): string | null => {
  const dateParsed = moment(date ?? moment(), [format, ...MOMENT_SUPPORTED_DATE_FORMATS], true);

  return dateParsed.isValid() ? dateParsed.format(format) : null;
};

export const dbLongDate = (date?: moment.MomentInput, format = `${ISO_DATE_FORMAT} HH:mm:ss`) =>
  moment(date ?? moment()).format(format);

export const nextQuarterEndDate = () => moment().add(2, 'months').endOf('month').format('MM/DD/YYYY');

export const isValidDate = (date: moment.MomentInput, format = MOMENT_SUPPORTED_DATE_FORMATS) =>
  moment(date ?? moment(), format, true).isValid();

export const endOfDay = (date: moment.MomentInput, format = ISO_DATE_FORMAT) =>
  moment(date ?? moment(), format)
    .endOf('day')
    .format(LONG_DATE_FORMAT);

export const formatDateView = (date: moment.MomentInput) => {
  if (isValidDate(date)) {
    return shortDate(date);
  }
  return date;
};

export const currentISODate = () => moment().format(ISO_DATE_FORMAT);

// Example: July 7, 2022
export const formatDateViewLL = (param: moment.MomentInput) => moment(param).format('LL');

// Extracts the date from a measurement date slug: 2022-08-30-1234 --> 2022-08-30
// We can get fancier with the default regular expression, but YYYY-MM-DD works for our usual format
export const extractDate = ({ dateSlug, format = /^\d{4}-\d{2}-\d{2}/ }: { dateSlug: string; format?: RegExp }) => {
  const match = format.exec(dateSlug);
  return match ? match[0] : null;
};

export const getYearFromIsoFormattedDate = (date: moment.MomentInput): string => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date as string)) {
    throw new Error('Invalid date format');
  }
  return (date as string).substring(0, 4);
};

export const formatDateTime = (dateParam: moment.MomentInput, options: FormatDateTimeOptions = {}) => {
  const {
    timeZone = options.timeZone ?? 'UTC',
    hour12 = options.hour12 ?? true,
    month = options.month ?? '2-digit',
    day = options.day ?? '2-digit',
    year = options.year ?? 'numeric',
    hour = options.hour ?? 'numeric',
    minute = options.minute ?? 'numeric',
  } = options;

  const date = moment(dateParam).toDate();
  const formattedDate = date.toLocaleDateString('en-US', {
    timeZone,
    hour12,
    month,
    day,
    year,
    hour,
    minute,
  });

  return formattedDate;
};

export const dateMinusOrPlusXPeriods = ({
  date,
  period,
  amount,
  format,
}: {
  date: moment.MomentInput;
  period: 'days' | 'weeks' | 'months' | 'years';
  amount: number;
  format: string;
}) =>
  moment(date)
    .add({
      [period]: amount,
    })
    .format(format);

export const getLocaleAgnosticDate = ({date }: {date: Date}) => ({
  month: String(date.getMonth() + 1).padStart(2, '0'),
  day: String(date.getDate()).padStart(2, '0'),
  year: date.getFullYear(),
});
